import MixpanelEvents from '@/enums/MixpanelEvents';
import config from '@/config/config';
import mixpanel from 'mixpanel-browser';
import { localeToReadableLanguageMap } from '@satellite/composables/useLocaleHelpers.ts';

class MixpanelPlugin {
  constructor(options) {
    this.track = this.trackMixpanelEvent.bind(this);
    this.init = this.initMixpanel.bind(this);
    this.events = MixpanelEvents;
    this.api = mixpanel;
    this.identify = this.setMixpanelIdentity.bind(this);
    this.reset = this.resetMixpanel.bind(this);
    this.startRecording = this.startSessionRecording.bind(this);
    this.stopRecording = this.stopSessionRecording.bind(this);
    this.pauseRecording = this.pauseSessionRecording.bind(this);
    this.resumeRecording = this.resumeSessionRecording.bind(this);
    this.isInitialized = false;
    this.getEntryPoint = this.getEntryPoint.bind(this);
    this.updateApplicationLanguageProperty = this.setApplicationLanguageProperty;
    this.debug = options?.debug; // Adding this so we can debug global properties easily
  }

  trackMixpanelEvent(event, data = {}) {
    if (import.meta.env.DEV || this.debug) {
      console.log('Tracking Mixpanel Event Debug:\n', { event, data });
    }

    // Silently ignore events if no token is set
    if (!config.mixpanel_token) {
      return;
    }

    if (this.debug) {
      console.log('Global Properties:', mixpanel.persistence.properties());
    }

    try {
      mixpanel.track(event, data);
    } catch (e) {
      console.log('MP Event Error', e);
    }
  }

  initMixpanel(selectedLanguage) {
    if (!config.mixpanel_token) {
      return;
    }

    this.isInitialized = true;
    mixpanel.init(config.mixpanel_token, { debug: false });
    this.updateApplicationLanguageProperty(selectedLanguage);
  }

  setMixpanelIdentity(me) {
    if (!config.mixpanel_token || !me) {
      return;
    }

    mixpanel.identify(me.email);
    mixpanel.people.set({
      $name: `${me.firstName} ${me.lastName}`,
      $email: me.email,
      'Nova User ID': me.id,
      'User Role': me.role,
      'Org ID': me.orgId
    });

    mixpanel.get_group('Org ID', me.orgId).set({
      Name: me.orgName,
      Type: me.orgType,
      'Created At': me.orgCreateDateTime,
      'TC Confirmed At': me.tcConfirmedAt
    });
  }

  resetMixpanel() {
    if (this.isInitialized) {
      mixpanel.reset();
    }
  }

  /**
   * Starts Mixpanel session recording if the plugin has been initialized.
   * @returns {void}
   */
  startSessionRecording() {
    if (this.isInitialized) {
      mixpanel.start_session_recording();
    }
  }

  /**
   * Stops Mixpanel session recording if the plugin has been initialized.
   * @returns {void}
   */
  stopSessionRecording() {
    if (this.isInitialized) {
      mixpanel.stop_session_recording();
    }
  }

  /**
   * Pauses Mixpanel session recording if the plugin has been initialized.
   * @returns {void}
   */
  pauseSessionRecording() {
    if (this.isInitialized) {
      mixpanel['pause_session_recording']();
    }
  }

  /**
   * Resumes Mixpanel session recording if the plugin has been initialized.
   * @returns {void}
   */
  resumeSessionRecording() {
    if (this.isInitialized) {
      mixpanel['resume_session_recording']();
    }
  }

  getEntryPoint(vueInstance, mappedEntryPoints) {
    for (let mappedEntryPoint of mappedEntryPoints) {
      let parent = vueInstance.$parent;
      while (parent) {
        if (parent.$options.name === mappedEntryPoint.component) {
          return mappedEntryPoint.entryPoint;
        }
        parent = parent.$parent;
      }
    }

    return 'Untraceable';
  }

  /**
   * Registers Mixpanel super properties if the plugin has been initialized.
   * @param {Record<string, unknown>} [properties={}]
   * @returns {void}
   */
  registerSuperProperties(properties = {}) {
    if (this.isInitialized) {
      mixpanel.register(properties);
    }
  }

  setApplicationLanguageProperty(selectedLanguage) {
    // Prevent errors here against mixpanel not initializing correctly
    if (!this.isInitialized || !this.api || !this.api.register) {
      console.log('Mixpanel not initialized yet');
      return;
    }
    mixpanel.register({ application_language: selectedLanguage ?? localeToReadableLanguageMap.en });
  }
}

export default MixpanelPlugin;
